import React, {
  Fragment,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import Diff, {
  LineAnnotation,
  getCurrentLineAnnotations,
  getOutdatedLineAnnotations,
} from '@atlassian/bitkit-diff';
import * as styles from '@atlassian/bitkit-diff/exports/styles';
import { FileStateless } from '@atlassian/bitkit-file';

import { DiffStatusIcon } from 'src/components/diff-file-status/diff-file-status-icon-mapper';
import { DiffConflictLozenge } from 'src/components/diff-set/diff-conflict-lozenge';
import { DiffMetadataProps } from 'src/components/diff-set/diff-set';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import {
  TOGGLE_DIFF_EXPANSION,
  expandContext,
  onEditorOpenStateChange,
  toggleDiffComments,
  toggleSideBySideMode,
} from 'src/redux/pull-request/actions';
import { getIsSingleFileModeActive } from 'src/redux/pull-request/selectors';
import { useContextExpansion } from 'src/sections/repository/sections/branch/hooks/expand-context';
import { DiffActionsMenu } from 'src/sections/repository/sections/pull-request/components/diff-actions-menu';
import { DiffFeaturesContext } from 'src/sections/repository/sections/pull-request/components/diff-features-context';
import diffMessages from 'src/sections/repository/sections/pull-request/components/diff.i18n';
import DiscardCommentsDialog, {
  useDiscardCommentsDialogState,
} from 'src/sections/repository/sections/pull-request/components/discard-comments-dialog';
import { FileLevelAddCommentButton } from 'src/sections/repository/sections/pull-request/components/file-level-comment/file-level-add-comment-button';
import { ReviewedCheckbox } from 'src/sections/repository/sections/pull-request/components/file-reviewed-checkbox/reviewed-checkbox';
import HiddenCommentsBanner from 'src/sections/repository/sections/pull-request/components/hidden-comments-banner';
import { useSingleDiffReviewed } from 'src/sections/repository/sections/pull-request/components/reviewed-files/reviewed-files-provider';
import { DiffStateContext } from 'src/sections/repository/sections/pull-request/components/tabs/diff/diff-state-context';
import { useIsViewEntireFileSupported } from 'src/sections/repository/sections/pull-request/components/view-entire-file/hooks';
import { PullRequestFileConversation } from 'src/sections/repository/sections/pull-request/containers/conversation/pull-request-file-conversation';
import OutdatedAnnotationsButton from 'src/sections/repository/sections/pull-request/containers/outdated-annotations-button';
import OutdatedCommentsButton from 'src/sections/repository/sections/pull-request/containers/outdated-comments-button';
import { usePullRequestInlineConversations } from 'src/sections/repository/sections/pull-request/hooks/conversation/pull-request-inline-conversations';
import {
  useAnnotations,
  useDiffPreferences,
  useHiddenComments,
  usePermalinks,
} from 'src/sections/repository/sections/pull-request/hooks/diffs';
import { getSyntaxHighlightingEnabled } from 'src/selectors/feature-selectors';
import { getCurrentUserIsAnonymous } from 'src/selectors/user-selectors';
import { Diff as DiffType } from 'src/types/pull-request';
import {
  extractFilepath,
  extractPrevFilepath,
} from 'src/utils/extract-file-path';

import PreviewMergeDiffIcon from '../diff-file-status/preview-merge-diff-icon';

import { useFileViewed } from './hooks/file-viewed';

export type Props = {
  file: DiffType;
  fileIndex: number;
  isOpen: boolean;
  diffMetadataProps: DiffMetadataProps;
};

export const DiffFile = (props: Props) => {
  const { file, fileIndex, diffMetadataProps, isOpen } = props;
  const { confirmDiscardComments, dialogProps, isDialogOpen } =
    useDiscardCommentsDialogState();

  const {
    hasMarkFilesAsViewed,
    hasComments,
    hasAnnotations,
    previewMergeOption,
    onSideBySideClicked,
    onShowCommentsClicked,
  } = useContext(DiffFeaturesContext);

  const { conflictMessage, fileDiffStatus, isConflicted } = file;
  const filepath = extractFilepath(file);
  const prevFilepath = extractPrevFilepath(file);
  const editorPopupsMountPointRef = useRef(null);
  const numInlineEditorsOpen = useRef(0);
  const intl = useIntl();
  const dispatch = useDispatch();

  const [isDiffActionsMenuOpen, setIsDiffActionsMenuOpen] = useState(false);

  const { isColorBlindModeEnabled, isSideBySide, tabSize } =
    useDiffPreferences(file);
  const { activePermalink, onPermalinkClick, showPermalinks } = usePermalinks();

  const isSyntaxHighlightingEnabled = useSelector(getSyntaxHighlightingEnabled);

  const isMultiChunkExpansionInDiffEnabled = useStatsigGate(
    StatsigFeatureKeys.isMultiChunkExpansionInDiffEnabled
  );

  const isViewEntireFileSupported = useIsViewEntireFileSupported(file);
  const isCurrentUserAnonymous = useSelector(getCurrentUserIsAnonymous);
  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);

  const { fileAnnotations, isPrAnnotationsEnabled } = useAnnotations(file);
  const lineAnnotations = getCurrentLineAnnotations(fileAnnotations);
  const outdatedLineAnnotations = getOutdatedLineAnnotations(fileAnnotations);

  const isReviewed = useSingleDiffReviewed(filepath);

  const areCommentsHidden = useHiddenComments(file);

  const { iterativeReviewSelectedDiff } = useContext(DiffStateContext);

  const handleDiffActionsMenuClicked = useCallback(
    (e: any) => setIsDiffActionsMenuOpen(!!e.isOpen),
    [setIsDiffActionsMenuOpen]
  );

  const onExpand = (filePath: string, open: boolean) => {
    dispatch({
      type: TOGGLE_DIFF_EXPANSION,
      payload: { filepath: filePath, isOpening: open },
    });
  };

  const handleShowCommentsClicked = useCallback(() => {
    if (areCommentsHidden) {
      dispatch(toggleDiffComments(filepath));
      if (onShowCommentsClicked) {
        onShowCommentsClicked();
      }
    }
  }, [onShowCommentsClicked, filepath, areCommentsHidden, dispatch]);

  const handleSideBySideClick = useCallback(() => {
    const activateSideBySideMode = () => {
      dispatch(
        toggleSideBySideMode({
          filePath: filepath,
          isSideBySide: !isSideBySide,
        })
      );
      if (onSideBySideClicked) {
        onSideBySideClicked();
      }
    };
    // Toggling SBS vs. Unified will cause a full re-render and wipe out the content in
    // any open comment editors (new threads) and close any replies
    if (numInlineEditorsOpen.current > 0) {
      confirmDiscardComments({
        message: diffMessages.discardCommentsModalBody__SideBySideToggle,
        onConfirmDiscard: activateSideBySideMode,
      });
    } else {
      activateSideBySideMode();
    }
  }, [
    confirmDiscardComments,
    filepath,
    isSideBySide,
    dispatch,
    onSideBySideClicked,
  ]);

  const dropdownMenu = (
    <DiffActionsMenu
      filepath={filepath}
      isDeleted={fileDiffStatus === 'removed'}
      isSideBySide={isSideBySide}
      isViewEntireFileSupported={isViewEntireFileSupported}
      onSideBySide={() => {
        onExpand(filepath, true);
        handleSideBySideClick();
      }}
      onDiffActionsMenuClicked={handleDiffActionsMenuClicked}
      diffMetadataProps={diffMetadataProps}
      isCollapsible={!isSingleFileModeActive}
    />
  );

  const getDiffStatusIcon = () => {
    return fileDiffStatus ? (
      <DiffStatusIcon fileDiffStatus={fileDiffStatus} />
    ) : undefined;
  };

  const shouldRenderConflictLozenge = () => {
    return isConflicted && conflictMessage
      ? () => <DiffConflictLozenge conflictMessage={conflictMessage} />
      : undefined;
  };

  const { onExpand: onExpandContext } = useContextExpansion({
    file,
    filepath,
    diffMetadataProps,
  });

  const handleShowMoreLines = useCallback(
    (expanderIndex: any, isShiftClicked: boolean, shouldExpandDown: any) => {
      if (!previewMergeOption) {
        onExpandContext(expanderIndex, isShiftClicked, shouldExpandDown);
      } else {
        const getMoreContextLines = () => {
          dispatch(
            expandContext({
              expanderIndex,
              fileIndex,
              filepath,
              prevFilepath,
              isShiftClicked,
              shouldExpandDown,
              selectedDiffSourceHash: iterativeReviewSelectedDiff.sourceHash,
              selectedDiffDestHash: iterativeReviewSelectedDiff.destHash,
            })
          );
        };

        // Context expansion will cause a full re-render and wipe out the content in any
        // open comment editors (new threads) and close any replies
        if (numInlineEditorsOpen.current > 0) {
          confirmDiscardComments({
            message: diffMessages.discardCommentsModalBody__ExpandContext,
            onConfirmDiscard: () => {
              getMoreContextLines();
            },
          });
        } else {
          getMoreContextLines();
        }
      }
    },
    [
      onExpandContext,
      previewMergeOption,
      confirmDiscardComments,
      dispatch,
      fileIndex,
      filepath,
      prevFilepath,
      iterativeReviewSelectedDiff.sourceHash,
      iterativeReviewSelectedDiff.destHash,
    ]
  );

  const handleInlineEditorClose = useCallback(() => {
    numInlineEditorsOpen.current--;
    if (numInlineEditorsOpen.current === 0) {
      dispatch(onEditorOpenStateChange(false));
    }
  }, [dispatch]);

  const handleInlineEditorOpen = useCallback(() => {
    numInlineEditorsOpen.current++;
    dispatch(onEditorOpenStateChange(true));
  }, [dispatch]);
  const {
    discardCommentsModal,
    onAddInlineComment,
    renderInlineConversations,
  } = usePullRequestInlineConversations({
    diff: file,
    editorPopupsMountPointRef,
    onInlineEditorClose: handleInlineEditorClose,
    onInlineEditorOpen: handleInlineEditorOpen,
  });

  const onFileViewed = useFileViewed();
  const onReviewedChanged = useCallback(
    () => onFileViewed(filepath, isReviewed, file.index),
    [filepath, isReviewed, file.index, onFileViewed]
  );

  return (
    <Fragment>
      <article
        data-qa="branch-diff-file"
        ref={editorPopupsMountPointRef}
        aria-label={intl.formatMessage(diffMessages.diffFileTitle, {
          filepath,
        })}
      >
        <FileStateless
          prevFilePath={prevFilepath}
          filePath={filepath}
          icon={getDiffStatusIcon()}
          previewMergeDiffIcon={
            previewMergeOption ? <PreviewMergeDiffIcon /> : undefined
          }
          dropdownMenu={dropdownMenu}
          isCollapsible={!isSingleFileModeActive}
          isReviewed={hasMarkFilesAsViewed ? isReviewed : undefined}
          isDropdownMenuOpen={isDiffActionsMenuOpen}
          isExpanded={isOpen}
          hasStickyHeader
          renderBeforeActions={() => (
            <>
              {hasAnnotations &&
              isPrAnnotationsEnabled &&
              outdatedLineAnnotations.length > 0 ? (
                <OutdatedAnnotationsButton
                  outdatedLineAnnotations={outdatedLineAnnotations}
                />
              ) : null}
              {hasComments ? (
                <OutdatedCommentsButton
                  file={file}
                  selectedDiffType={
                    iterativeReviewSelectedDiff.selectedDiffType
                  }
                />
              ) : null}
              {hasMarkFilesAsViewed ? (
                <ReviewedCheckbox
                  filePath={filepath}
                  onChange={onReviewedChanged}
                />
              ) : null}
              {!isCurrentUserAnonymous && hasComments ? (
                <FileLevelAddCommentButton filePath={filepath} />
              ) : null}
            </>
          )}
          toggleExpanded={() => {
            onExpand(filepath, !isOpen);
          }}
          renderAfterFilePath={shouldRenderConflictLozenge()}
        >
          {hasAnnotations &&
          isPrAnnotationsEnabled &&
          lineAnnotations.length ? (
            <styles.TopLevelInlineContent>
              <LineAnnotation lineAnnotations={lineAnnotations} />
            </styles.TopLevelInlineContent>
          ) : null}
          {hasComments && (
            <PullRequestFileConversation
              editorPopupsMountPoint={editorPopupsMountPointRef.current}
              file={file}
            />
          )}
          {hasComments && (
            <HiddenCommentsBanner
              visible={!areCommentsHidden}
              showComments={handleShowCommentsClicked}
            />
          )}
          <Diff
            activePermalink={activePermalink}
            diff={{ chunks: file.chunks }}
            fileAnnotations={hasAnnotations ? fileAnnotations : undefined}
            filePath={filepath}
            isColorBlindModeEnabled={isColorBlindModeEnabled}
            isPrAnnotationsEnabled={
              hasAnnotations ? isPrAnnotationsEnabled : undefined
            }
            isSideBySide={isSideBySide}
            onPermalinkClick={onPermalinkClick}
            showPermalinks={showPermalinks}
            tabSize={tabSize}
            onShowMoreLines={handleShowMoreLines}
            isSyntaxHighlightingEnabled={isSyntaxHighlightingEnabled}
            inlineContent={hasComments ? renderInlineConversations : undefined}
            onAddComment={hasComments ? onAddInlineComment : undefined}
            isMultiChunkExpansionInDiffEnabled={
              isMultiChunkExpansionInDiffEnabled
            }
          />
        </FileStateless>
      </article>
      {/* This modal enforces 1 open inline comment editor at a time */}
      {discardCommentsModal}
      {/* This modal catches dropdown menu actions: SBS/Unified mode switches and context expansion */}
      <ModalTransition>
        {isDialogOpen && <DiscardCommentsDialog {...dialogProps} />}
      </ModalTransition>
    </Fragment>
  );
};
